import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import ROUTES, { SWITCH_ROUTES } from '@constants/routes';
import { FaPeopleArrows, FaMobile, FaLock, FaEnvelope } from 'react-icons/fa';
import { TfiReload } from 'react-icons/tfi';
import { Button, Form, Input, Spin } from 'antd';
import { useGlobalProps } from '@hooks/useGlobalProps';
import {
    FONT_SIZE,
    MAX_PAD_WIDTH,
    PASSWORD_MAX_LENGTH,
} from '@constants/numbers';
import { getDVCInLS } from '@utils/ls';
import { STATUS_CODE } from '@constants/api';
import AlertModal from '@components/shared/modals/AlertModal';
import { agentApply } from '@api/user';
import CaptchaView, { generateCaptcha } from '@components/shared/CaptchaView';
import SimpleLangSelector from '@components/header/SimpleLangSelector';
import { useLocation } from 'react-router-dom';


const INIT_ALERT = {
    show: false,
    title: '',
    message: '',
    children: null,
};

export default function JoinUsPage() {
    const [errorMsg, setErrorMsg] = useState('');
    const [alert, setAlert] = useState({ ...INIT_ALERT });
    const [captchaAnswer, setCaptchaAnswer] = useState(generateCaptcha());
    const [captchaV, setCaptchaV] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFieldsLocked, setIsFieldsLocked] = useState(false);
    const [resendCountdown, setResendCountdown] = useState(0);
    const [affiliateCode, setAffiliateCode] = useState('');
    const [form] = Form.useForm();

    const { isLiveChatOn, isTelegramOn, telegramAccount, isWhatsappOn, whatsappAccount } = useGlobalProps();
    const { t } = useTranslation();

    const tUser = t('user', { returnObjects: true });
    const tCommon = t('common', { returnObjects: true });
    const tError = t('error', { returnObjects: true });
    const tModal = t('modal', { returnObjects: true });
    
    const closeAlert = () => setAlert({ ...INIT_ALERT });
    const reloadCaptcha = () => setCaptchaAnswer(generateCaptcha());

    const location = useLocation();
    
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const mValue = params.get('m');
        if (mValue) {
            setAffiliateCode(mValue);
            form.setFieldsValue({ affiliate: mValue });
        }
    }, [form, location.search]);

    // Countdown timer for resend functionality
    useEffect(() => {
        let timer;
        if (resendCountdown > 0) {
            timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
        }
        return () => clearTimeout(timer);
    }, [resendCountdown]);

    // Validate Gmail format
    const isValidGmail = (email) => {
        return email && email.toLowerCase().endsWith('@gmail.com');
    };

    // Validate 6-digit password
    const isValidPassword = (password) => {
        return password && /^\d{6}$/.test(password);
    };

    // #region API
    const handleSubmit = async (values) => {
        setErrorMsg('');
        
        const { email, mobile, password, captcha } = values;

        // Validation
        if (!email || !mobile || !password || !captcha) {
            setErrorMsg(tUser['joinus@fill-all-required']);
            return;
        }

        if (!isValidGmail(email)) {
            setErrorMsg(tUser['joinus@invalid-gmail']);
            return;
        }

        if (!isValidPassword(password)) {
            setErrorMsg(tUser['joinus@invalid-password']);
            return;
        }

        if (captchaAnswer.toLowerCase() !== captcha.toLowerCase()) {
            setErrorMsg(tError['error@wrong-captcha']);
            return;
        }

        setIsSubmitting(true);

        try {
            const req = {
                gmail: email.trim(),
                pwd: password,
                tel: mobile.trim(),
                dvc: getDVCInLS()
            };
            
            // Add affiliate code if available
            if (affiliateCode) {
                req.intro = affiliateCode;
            }

            const res = await agentApply(req);

            if (res && res.rtn === STATUS_CODE.SUCCESS) {
                // Lock fields after successful submission
                setIsFieldsLocked(true);
                setResendCountdown(30); // 30 second countdown for resend
                
                setAlert({
                    show: true,
                    title: tUser['joinus@application-success'],
                    message: tUser['joinus@application-success-message'],
                });
            } else {
                setErrorMsg(res.msg || tUser['joinus@application-failed']);
            }
        } catch (error) {
            console.error('Agent apply error:', error);
            setErrorMsg(tUser['joinus@application-failed']);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResend = async () => {
        if (resendCountdown > 0) return;

        const values = form.getFieldsValue();
        
        if (!values.email || !values.mobile || !values.password) {
            setErrorMsg(tUser['joinus@complete-form-first']);
            return;
        }

        setIsSubmitting(true);
        
        try {
            const req = {
                gmail: values.email.trim(),
                pwd: values.password,
                tel: values.mobile.trim(),
                dvc: getDVCInLS()
            };
            
            // Add affiliate code if available
            if (affiliateCode) {
                req.intro = affiliateCode;
            }

            const res = await agentApply(req);
            
            if (res && res.rtn === STATUS_CODE.SUCCESS) {
                setResendCountdown(30); // Reset countdown
                setAlert({
                    show: true,
                    title: tUser['joinus@resend-success'],
                    message: tUser['joinus@resend-success-message'],
                });
            } else {
                setErrorMsg(res.msg || tUser['joinus@resend-failed']);
            }
        } catch (error) {
            console.error('Resend error:', error);
            setErrorMsg(tUser['joinus@resend-failed']);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <RegisterPageWrap>
            <Helmet>
                <title>JOIN US - Agent Application</title>
            </Helmet>
            <div className="register-card">
                <div className="lang-selector-wrap">
                    <SimpleLangSelector />
                </div>
                
                <div className="register-card-head">
                    <h1>{tUser['joinus@title']}</h1>
                </div>

                <Form
                    form={form}
                    className="register-card-form"
                    onFinish={handleSubmit}
                    layout="vertical"
                >

                    {affiliateCode && (
                        <Form.Item
                            name="affiliate"
                            initialValue={affiliateCode}
                        >
                            <InputWithIcon
                                size="large"
                                placeholder={tUser['user@affiliate']}
                                value={affiliateCode}
                                disabled={true}
                                prefix={<FaPeopleArrows />}
                            />
                        </Form.Item>
                    )}     

                    <Form.Item
                        name="mobile"
                        rules={[{ required: true, message: tUser['joinus@mobile-required'] }]}
                    >
                        <InputWithIcon
                            size="large"
                            placeholder={tUser['joinus@mobile-placeholder']}
                            disabled={isFieldsLocked}
                            prefix={<FaMobile />}
                        />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        rules={[
                            { required: true, message: tUser['joinus@gmail-required'] },
                            { 
                                validator: (_, value) => {
                                    if (!value || isValidGmail(value)) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(tUser['joinus@invalid-gmail']));
                                }
                            }
                        ]}
                    >
                        <InputWithIcon
                            size="large"
                            placeholder={tUser['joinus@gmail-placeholder']}
                            disabled={isFieldsLocked}
                            prefix={<FaEnvelope />}
                        />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        rules={[
                            { required: true, message: tUser['joinus@password-required'] },
                            { 
                                validator: (_, value) => {
                                    if (!value || isValidPassword(value)) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(tUser['joinus@invalid-password']));
                                }
                            }
                        ]}
                        help={tUser['hint@password']}
                    >
                        <InputWithIcon
                            size="large"
                            placeholder={tUser['joinus@password-placeholder']}
                            type="password"
                            disabled={isFieldsLocked}
                            prefix={<FaLock />}
                            maxLength={6}
                        />
                    </Form.Item>

                    <CaptchaSection>
                        <div className="captcha-display">
                            <CaptchaView captchaValue={captchaAnswer} />
                        </div>
                        <div className="captcha-display">
                        <TfiReload onClick={reloadCaptcha} className="reload-icon" />
                        <Form.Item
                            name="captcha"
                            rules={[{ required: true, message: tUser['joinus@captcha-required'] }]}
                            style={{ marginBottom: 0, width: '100%' }}
                        >
                            <Input
                                size="large"
                                placeholder={tUser['joinus@captcha-placeholder']}
                                disabled={isFieldsLocked}
                            />
                        </Form.Item>
                        </div>
                    </CaptchaSection>

                    {errorMsg && <ErrorMessage>{errorMsg}</ErrorMessage>}

                    <Form.Item style={{ marginBottom: 0 }}>
                        {isFieldsLocked ? (
                            <ResendSection>
                                <SubmitButton
                                    size="large"
                                    onClick={handleResend}
                                    disabled={resendCountdown > 0 || isSubmitting}
                                    loading={isSubmitting}
                                >
                                    {resendCountdown > 0 ? tUser['joinus@resend-countdown'].replace('{seconds}', resendCountdown) : tUser['joinus@resend-button']}
                                </SubmitButton>
                                <p>{tUser['joinus@application-submitted']}</p>
                            </ResendSection>
                        ) : (
                            <SubmitButton
                                type="primary"
                                htmlType="submit"
                                size="large"
                                loading={isSubmitting}
                                disabled={isSubmitting}
                            >
                                {tCommon['common@submit']}
                            </SubmitButton>
                        )}
                    </Form.Item>
                </Form>
            </div>

            <AlertModal
                show={alert.show}
                title={alert.title}
                message={alert.message}
                onConfirm={alert.onConfirm || closeAlert}
            />
        </RegisterPageWrap>
    );
}

// Styled Components
const RegisterPageWrap = styled.div`
    display: flex;
    background: url('/images/new_background.jpg') center center / cover no-repeat,
                radial-gradient(ellipse at center, #1e3c72 0%, #2a5298 50%, #0f1419 100%);
    background-size: cover;
    background-repeat: no-repeat;
    width: 100%;
    height: 100vh;
    justify-content: center;
    align-items: center;
    padding: 20px;

    .register-card {
        position: relative;
        background: #fff;
        max-height: 90vh;
        width: 95vw;
        overflow-y: auto;
        box-shadow: 0 .5rem 1rem rgba(0,0,0,.15);
        border-radius: 0.375rem;
        padding: 48px;
        
        @media (min-width: ${MAX_PAD_WIDTH}px) {
            max-width: 500px;
        }

        &-head {
            display: flex;
            justify-content: center;
            align-items: center;
            margin-bottom: 32px;

            h1 {
                font-size: 48px;
                font-weight: 700;
                color: #666;
                margin: 0;
                letter-spacing: 2px;
            }
        }

        &-form {
            .ant-form-item {
                margin-bottom: 24px;
            }
        }

        .register-form-affiliate {
        margin-bottom: 10px !important;
        >div {
            >div {
                >div:nth-child(2) {
                    display: none !important;
                }
            }
        }
        span, input {
            color: black;
        }
    }

    }

    .lang-selector-wrap {
        position: absolute;
        top: 15px;
        right: 20px;
    }
`;

const InputWithIcon = styled(Input)`
    height: 50px !important;
    border: 1px solid #ddd !important;
    padding-left: 70px !important;
    font-size: 16px !important;
    position: relative;

    .ant-input-prefix {
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        width: 60px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        z-index: 2;
        border-right: 1px solid #ddd;
    }

    &:disabled {
        background-color: #f5f5f5;
        color: #999;
    }

    &::placeholder {
        color: #ccc;
    }
`;

const CaptchaSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 24px;

    .captcha-display {
        display: flex;
        align-items: center;
        gap: 12px;
        justify-content: center;
    }

    .reload-icon {
        width: 24px;
        height: 24px;
        cursor: pointer;
        color: #666;
        flex-shrink: 0;

        &:hover {
            color: #1890ff;
        }
    }

    .ant-form-item {
        margin-bottom: 0;
    }

    input {
        height: 50px !important;
        border: 1px solid #ddd !important;
        padding: 0 20px !important;
        font-size: 16px !important;
    }
`;

const ErrorMessage = styled.p`
    color: #ff4d4f;
    font-size: 14px;
    margin: 8px 0 16px 0;
    text-align: center;
`;

const SubmitButton = styled(Button)`
    width: 100% !important;
    height: 50px !important;
    font-size: 18px !important;
    font-weight: 600 !important;
    border: none !important;
    background: ${({ theme }) => theme.primary} !important;
    color: ${({ theme }) => theme.text} !important;
    margin-top: 16px !important;

    &:hover, &:focus {
        background: ${({ theme }) => `${theme.primary}dd`} !important; /* Slightly darker with alpha */
        color: ${({ theme }) => theme.text} !important;
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba(0,0,0,0.1);
    }

    &:disabled {
        background: #ccc !important;
        color: ${({ theme }) => theme.text} !important; 
    }
`;

const ResendSection = styled.div`
    text-align: center;

    p {
        margin-top: 12px;
        color: #666;
        font-size: 14px;
    }
`;